'use strict';

import { fh_polygon } from "@acenv/fh-3d-viewer";
import { CN_CURRENT_DATE, cn_transaction_manager } from "../utils/cn_transaction_manager";
import { cn_add, cn_box, cn_compare_polygons, cn_dist, cn_dot, cn_intersect_line, cn_manhattan_distance, cn_middle, cn_mul, cn_normal, cn_normalize, cn_polar, cn_random, cn_seed, cn_sub, cnx_add, cnx_clone, cnx_intersect_line, cnx_mul, cnx_sub } from "../utils/cn_utilities";
import { cn_scene } from "./cn_scene";
import { CN_MIDDLE, cn_wall } from "./cn_wall";
import { cn_wall_type } from "./cn_wall_type";
import { cn_material } from "./cn_material";
import { cn_vertex } from "./cn_vertex";
import { cn_opening } from "./cn_opening";
import { logger } from "../utils/cn_logger";
import { cn_building } from "./cn_building";
import { cn_space } from "./cn_space";
import { cn_balcony_type } from "./cn_balcony_type";
import { cn_fence_type } from "./cn_fence_type";

//***********************************************************************************
//***********************************************************************************
//******     CN-Map    **************************************************************
//******     Copyright(C) 2019-2024 EnerBIM                        ******************
//***********************************************************************************
//***********************************************************************************

const CNA_PARALLEL_THERSHOLD = 0.95;

/**
 * Cna wall : the class tro manage a wxall inside the analyser.
 */
class cna_wall {
    /**
     *
     * @param {cn_wall} wall
     */
    constructor(wall) {
        this.wall = wall;
        this.thickness = 0;
        this.valid = true;
        this.dirty = false;
        this.vertices = [[0, 0], [0, 0]];
        this.directions = [[0, 0], [0, 0]];
        this.length = 0;
        this.shape_vertices = [];
        /**
         * @type {fh_polygon}
         */
        this.shape = null;

        /**
         * @type {fh_polygon}
         */
        this.extended_shape = null;

        /**
         * @type {cna_wall[]}
         */
        this.intersections = [];

        /**
         * @type {cna_wall[]}
         */
        this.wall_support = [null, null]
    }

    //*** Compute wall geometry */
    update() {
        this.thickness = this.wall.wall_type.thickness;
        const p0 = this.wall.vertex_position(0);
        const p1 = this.wall.vertex_position(1);
        const dir = cn_sub(p1, p0);
        const l = cn_normalize(dir);
        if (l < 0.01) {
            this.valid = false;
            this.dirty = true;
        }
        this.valid = true;
        this.thickness = this.wall.wall_type.thickness;
        const vertices = this.wall.shape.map(p => cnx_clone(p));
        if (this.shape_vertices.length != 0 && cn_dist(p0, this.vertices[0]) < 0.001 && cn_dist(p1, this.vertices[1]) < 0.001 && !vertices.some((v, i) => cn_dist(v, this.shape_vertices[i]) > 0.001))
            return;
        this.dirty = true;
        this.vertices = [p0, p1];
        this.directions = [dir, cn_mul(dir, -1)];
        this.length = l;
        this.shape_vertices = vertices;
        this.shape = new fh_polygon([0, 0, 0], [0, 0, 1]);
        this.shape.add_contour(vertices);
        if (this.thickness > 0.001) {
            this.extended_shape = this.shape.clone();
            this.extended_shape.offset(0.01);
        }
        else {
            const contour = [];
            const dx = cn_mul(this.directions[0], 0.01);
            const dy = cn_normal(dx);
            for (var i = 0; i < 4; i++) {
                let p = cnx_clone(vertices[i]);
                if (i < 2)
                    p = cnx_sub(p, dx);
                else
                    p = cnx_add(p, dx);
                if (i == 0 || i == 3)
                    p = cnx_sub(p, dy);
                else
                    p = cnx_add(p, dy);
                contour.push(p);
            }
            this.extended_shape = new fh_polygon([0, 0, 0], [0, 0, 1]);
            this.extended_shape.add_contour(contour);
            this.extended_shape.compute_contours();
        }
    }
}

//***********************************************************************************
//***********************************************************************************
//**** cn_scene_analyser : update walls and spaces in a scene.
//***********************************************************************************
//***********************************************************************************

export class cn_scene_analyser {
    /**
     *
     * @param {cn_scene} scene
     */
    constructor(scene) {
        /**
         * @type {cn_scene}
         */
        this.scene = scene;

        /**
         * @type {cn_building}
         */
        this.building = scene.parent;

        this.analysis_date = -1;
        this.check_analysis_date = -1;

        /**
         * @type {cna_wall[]}
         */
        this.wall_objects = [];
        this.test_number = -1;
    }

    /**
     * Up to date checking
     * @param {number} date
     * @returns {boolean}
     */
    up_to_date(date) {
        return date >= this.analysis_date;
    }

    /**
     *
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    process(transaction_manager = null) {

        this._update_walls_and_vertices(transaction_manager);

        //***  datation check */
        if (this._check_datation()) return;
        this.analysis_date = CN_CURRENT_DATE;

        this._analyse_walls(transaction_manager);
        this._analyse_spaces(transaction_manager);
    }

    /**
     * Update a list of tmp walls (not yet in the scene)
     * @param {cn_wall[]} walls
     */
    update_tmp_walls(walls) {
        walls.forEach(w => {
            w.build_self();
            w.footprint = null;
        });

        const out_walls = walls.filter(w => !this.scene.walls.includes(w));
        out_walls.forEach(w => {
            w.vertices[0].walls.push(w);
            w.vertices[1].walls.push(w);
        });

        walls.forEach(w => w.adjust_shapes());

        out_walls.forEach(w => {
            const i0 = w.vertices[0].walls.indexOf(w);
            if (i0 >= 0) w.vertices[0].walls.splice(i0, 1);
            const i1 = w.vertices[1].walls.indexOf(w);
            if (i1 >= 0) w.vertices[1].walls.splice(i1, 1);
        });

        walls.forEach(w => w.update_openings());
    }

    _check_datation() {
        const analysis_date = this.analysis_date;
        if (analysis_date < 0) return false;

        //*** fast statement */
        if (this.scene.parent.up_to_date(this.check_analysis_date)) return true;
        this.check_analysis_date = CN_CURRENT_DATE;

        //*** second fast statemebt */
        if (this.scene.parent.up_to_date(analysis_date))
            return true;

        //*** wall existence check */
        if (!this.scene.up_to_date(analysis_date, "walls")) return false;

        //*** wall geometry check */
        if (!this.scene.up_to_date(analysis_date, "vertices")) return false;
        if (this.scene.vertices.some(v => !v.up_to_date(analysis_date, "position") || !v.up_to_date(analysis_date, "support_wall") || !v.up_to_date(analysis_date, "support_wall_position"))) return false;
        if (this.scene.walls.some(w => !w.up_to_date(analysis_date, "vertices") || !w.up_to_date(analysis_date, "axis") || !w.up_to_date(analysis_date, "supported_vertices"))) return false;
        if (this.wall_objects.some(w => w.thickness != w.wall.wall_type.thickness)) return false;

        //*** nothing has changed! */
        return true;
    }

    /** Apply a partition of walls */
    analyse_wall_selection(kept_wall_ids) {
        this.scene.vertices.forEach(v => v.walls = []);

        this.scene.walls.forEach(w => {
            w.build_self();
            w.vertices[0].walls.push(w);
            w.vertices[1].walls.push(w);
        });

        this.scene.walls.forEach(w => w.adjust_shapes());

        const new_vertices = [];
        this.scene.vertices.filter(v => v.walls.length == 2 && (kept_wall_ids.includes(v.walls[0].ID) != kept_wall_ids.includes(v.walls[1].ID))).forEach(v => {
            const kept_wall = (kept_wall_ids.includes(v.walls[0].ID)) ? v.walls[0] : v.walls[1];
            const vi = kept_wall.vertex_index(v);
            const dir = kept_wall.bounds.direction;
            if (vi == 0) {
                const x0 = cn_dot(dir, cn_sub(v.position, kept_wall.shape[0]));
                const x1 = cn_dot(dir, cn_sub(v.position, kept_wall.shape[1]));
                let x = Math.max(x0, x1);
                v.position = cn_sub(v.position, cn_mul(dir, x));
            }
            else {
                const x0 = cn_dot(dir, cn_sub(kept_wall.shape[2], v.position));
                const x1 = cn_dot(dir, cn_sub(kept_wall.shape[3], v.position));
                let x = Math.max(x0, x1);
                v.position = cn_add(v.position, cn_mul(dir, x));
            }
            new_vertices.push(v);
        });

        this.scene.walls = this.scene.walls.filter(w => kept_wall_ids.includes(w.ID));
    }

    /**
     * Update walls and vertices
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    _update_walls_and_vertices(transaction_manager) {
        let changes = false;

        //*** Initiate vertices */
        this.scene.vertices.forEach(v => v.walls = []);
        this.scene.walls.forEach(w => {
            w.build_self();
            w.vertices[0].walls.push(w);
            w.vertices[1].walls.push(w);
        });

        function compute_priority(wall) {
            let p = 0;
            if (wall.wall_type.constructor == cn_wall_type || wall.wall_type.constructor == cn_fence_type)
                p = (wall.wall_type.free) ? 3 : 4;
            else if (wall.wall_type.constructor == cn_balcony_type)
                p = 2;
            return p;
        }

        //*** avoid vertices with more than two walls */
        this.scene.vertices.filter(v => v.walls.length > 2).forEach(v => {
            let wall0 = v.walls[0];
            let wall1 = null;
            let best_score = 0;
            for (var k = 0; k < v.walls.length; k++) {
                let w0 = v.walls[k];
                const p0 = compute_priority(w0);
                for (var k1 = k + 1; k1 < v.walls.length; k1++) {
                    let w1 = v.walls[k1];
                    const p1 = compute_priority(w1);
                    if (p1 != p0) continue;
                    let score = 1;
                    if (w0.wall_type == w1.wall_type) {
                        score = 2;
                        const a0 = cn_polar(w0.direction_from(v))[1];
                        const a1 = cn_polar(w1.direction_from(v))[1];
                        let delta = Math.abs(a0 - a1) / (2 * Math.PI);
                        while (delta > 1) delta--;
                        score += 2 - Math.abs(delta - 0.5) - 2 * Math.min(Math.abs(delta - 0.25), Math.abs(delta - 0.5), Math.abs(delta - 0.75));
                    }
                    if (score > best_score) {
                        best_score = score;
                        wall0 = w0;
                        wall1 = w1;
                    }
                }
            }
            const loose_vertices = [];
            v.walls.filter(w => w != wall0 && w != wall1).forEach(w => {
                const vi = w.vertex_index(v);
                const vertex = new cn_vertex(v.position, false, this.scene);
                loose_vertices.push(vertex);
                if (transaction_manager) transaction_manager.push_item_set(this.scene, "vertices");
                this.scene.vertices.push(vertex);
                if (transaction_manager) transaction_manager.push_item_set(w, "vertices");
                w.vertices[vi] = vertex;
                vertex.walls = [w];
            });
            v.walls = [wall0];
            if (wall1) v.walls.push(wall1);
            v.walls.forEach(w => w.adjust_shape(w.vertex_index(v)));

            //*** Attach loose vertices to wall0 or wall1. */
            loose_vertices.forEach(lv => {
                if (!lv.check_support_wall(wall0, transaction_manager) && wall1)
                    lv.check_support_wall(wall1, transaction_manager)
            });
        });

        //*** remove 0 length walls */
        let new_walls = this.scene.walls.filter(w => {
            if (w.bounds.length >= 0.001) return true;
            w.vertices.forEach(v => v.walls.splice(v.walls.indexOf(w), 1));
            let v0 = w.vertices[0];
            let v1 = w.vertices[1];
            if (v0.walls.length && v1.walls.length) {
                let i0 = v0.walls[0].vertex_index(v0);
                v0.walls[0].vertices[i0] = v1;
                v1.walls.push(v0.walls[0]);
                v0.walls = [];
            }
            else if (v0.walls.length && v1.support_wall)
                v0.support_wall = v1.support_wall;
            else if (v1.walls.length && v0.support_wall)
                v1.support_wall = v0.support_wall;
            return false;
        });
        if (new_walls.length < this.scene.walls.length) {
            if (transaction_manager) transaction_manager.push_item_set(this.scene, "walls");
            this.scene.walls = new_walls;
            changes = true;
        }

        //*** Fix heterogeneously connected vertices */
        this.scene.vertices.filter(v => v.walls.length == 2).forEach(v => {
            const p0 = compute_priority(v.walls[0]);
            const p1 = compute_priority(v.walls[1]);
            if (p0 == p1) {
                v.walls.forEach(w => w.adjust_shape(w.vertex_index(v)));
            }
            else {
                const weak_index = (p0 > p1) ? 1 : 0;
                const weak_wall = v.walls[(p0 > p1) ? 1 : 0];
                v.walls.splice(weak_index, 1);
                const vi = weak_wall.vertices.indexOf(v);
                const new_vertex = new cn_vertex(v.position, false, this.scene);
                new_vertex.support_wall = v.walls[0];
                new_vertex.walls = [weak_wall];
                if (transaction_manager) transaction_manager.push_item_set(this.scene, "vertices");
                this.scene.vertices.push(new_vertex);
                if (transaction_manager) transaction_manager.push_item_set(weak_wall, "vertices");
                weak_wall.vertices[vi] = new_vertex;
            }
        });

        //*** Remove vertices with no walls */
        if (this.scene.vertices.find(v => v.walls.length == 0)) {
            if (transaction_manager) transaction_manager.push_item_set(this.scene, "vertices");
            this.scene.vertices = this.scene.vertices.filter(v => v.walls.length);
            changes = true;
        }

        //*** manage supported vertices */
        const wrong_vertices = [];
        this.scene.vertices.filter(v => v.support_wall).forEach(v => {

            //*** Remove support if not compatible */
            if (!this.scene.walls.includes(v.support_wall) ||
                v.walls.length != 1 ||
                (v.support_wall_side > 1 && v.support_wall.vertices[v.support_wall_side - 2].walls.length == 2)) {
                v.set_support_wall(null, 0, transaction_manager);
                changes = true;
            }
            //*** Check support wall */
            else {
                if (!v.support_wall.supported_vertices.includes(v))
                    v.support_wall.supported_vertices.push(v);
                const wall_side = v.support_wall.get_side(v.support_wall_side);
                const normal = cn_normal(wall_side[1])
                //*** Check if support has moved */
                if (Math.abs(cn_dot(normal, cn_sub(wall_side[0], v.position))) <= 0.001)
                    v.walls[0].adjust_shape(v.walls[0].vertex_index(v))
                else
                    wrong_vertices.push(v);
            }
        });

        //*** try to fix wrong vertices */
        wrong_vertices.forEach(v => {
            v.check_support_wall(v.support_wall, transaction_manager);
            changes = true;
        });

        //*** try to attach loose vertices */
        this.scene.vertices.filter(v => v.walls.length == 1 && v.support_wall == null).forEach(v => {
            const wall = this.scene.find_wall(v.position, 0.01, v.walls);
            if (wall && v.check_support_wall(wall, transaction_manager))
                changes = true;
        });

        this.scene.walls.forEach(w => {
            w.update_openings();
            w.space_segments.forEach(ss => ss.update_wall_positions());
        });
        return changes;
    }

    _update_wall_objects() {
        const new_wall_objects = [];
        this.scene.walls.forEach(w => {
            let wall_object = this.wall_objects.find(wo => wo.wall == w);
            if (!wall_object) wall_object = new cna_wall(w);
            wall_object.update();
            wall_object.dirty = true; //*** TODO : remove this when datation is OK */
            new_wall_objects.push(wall_object);
        });
        this.wall_objects = new_wall_objects;
    }

    _cleanse_walls() {
        this.wall_objects.filter(w => !w.valid).forEach(w => {
            const wi = this.scene.walls.indexOf(w.wall);
            if (wi >= 0) this.scene.walls.splice(wi, 1);
        });
        this.wall_objects = this.wall_objects.filter(w => w.valid);
    }

    /**
     * Performs wall analysis
     * @param {cn_transaction_manager} transaction_manager
     */
    _analyse_walls(transaction_manager) {
        //*** update wall objects */
        this._update_wall_objects();

        //*** last datation check. Maybe no wall has changed its geometry */
        if (!this.wall_objects.some(w => w.dirty))
            return;

        //*** sort walls by priority */
        this.wall_objects.sort((wp0, wp1) => {

            //*** The priority is the one that's not a balcony */
            if (wp0.wall.balcony != wp1.wall.balcony)
                return wp1.wall.balcony ? -1 : 1;

            //*** The priority is the one that's not free */
            if (wp0.wall.wall_type.free != wp1.wall.wall_type.free)
                return wp1.wall.wall_type.free ? -1 : 1;

            //*** The priority is the thickest */
            if (wp0.wall.wall_type.thickness == wp1.wall.wall_type.thickness) return 0;
            return (wp0.wall.wall_type.thickness > wp1.wall.wall_type.thickness) ? -1 : 1;
        });

        //*** clear data */
        this.wall_objects.forEach(wp => {
            wp.intersections = [];
            wp.wall_support = [null, null];
        });

        //*** Manage overlapping objects */
        if (false) {
            for (var n0 = 0; n0 < this.wall_objects.length; n0++) {
                const wp0 = this.wall_objects[n0];
                if (!wp0.dirty || !wp0.wall) continue;

                //*** Loop on previous intersecting objects */
                for (var n1 = 0; n1 < n0; n1++) {
                    const wp1 = this.wall_objects[n1];
                    if (!wp1.wall) continue;

                    //*** end if common vertex */
                    if (wp0.wall.vertices.includes(wp1.wall.vertices[0]) || wp0.wall.vertices.includes(wp1.wall.vertices[1]))
                        continue;

                    //*** Overlaping walls */
                    if (Math.abs(cn_dot(wp0.directions[0], wp1.directions[1])) > CNA_PARALLEL_THERSHOLD) {
                        this._manage_overlapping(wp0, wp1, transaction_manager);
                        if (!wp0.wall) break;
                        continue;
                    }
                }
            }
            this.wall_objects = this.wall_objects.filter(wp => wp.wall);
        }

        //*** Loop on dirty objects */
        for (var n0 = 0; n0 < this.wall_objects.length; n0++) {
            const wp0 = this.wall_objects[n0];
            if (!wp0.dirty) continue;

            //*** Loop on previous intersecting objects */
            for (var n1 = 0; n1 < n0; n1++) {
                const wp1 = this.wall_objects[n1];

                //*** end if common vertex */
                if (wp0.wall.vertices.includes(wp1.wall.vertices[0]) || wp0.wall.vertices.includes(wp1.wall.vertices[1]))
                    continue;

                //*** end if no intersection */
                const intersection = wp0.extended_shape.clone();
                intersection.intersects(wp1.extended_shape);
                if (intersection.get_area() == 0) continue;

                wp0.intersections.push(wp1);
            }
        }

        //*** Finalize */
        this.wall_objects.forEach(wp => wp.update());

        this.wall_objects.forEach(wp => {
            const footprint = (wp.intersections.length) ? wp.shape.clone() : wp.shape;
            wp.intersections.forEach(i => footprint.substracts(i.shape));
            if (!cn_compare_polygons(wp.wall.footprint, footprint)) {
                if (transaction_manager) transaction_manager.push_item_set(wp.wall, "footprint");
                wp.wall.footprint = footprint;
            }
        });
    }

    /**
     * Manage the case where WP0 is behind (or partially) WP1.
     * @param {cna_wall} wp0
     * @param {cna_wall} wp1
     * @param {cn_transaction_manager} transaction_manager
     * @returns
     */
    _manage_overlapping(wp0, wp1, transaction_manager) {

        //*** Compute intersection of this wall to the other footprint */
        const p0 = wp0.vertices[0];
        const p1 = wp0.vertices[1];
        const segments = wp1.extended_shape.intersect_segment(cnx_clone(p0), cnx_clone(p1));

        //*** End if no overlapping */
        if (segments.length == 0) return;

        //*** Special case : the wall is entirely below the other */
        if (segments.length == 2 && cn_dist(segments[0], p0) < 0.01 && cn_dist(segments[1], p1) < 0.01) {
            this.scene.remove_wall(wp0.wall, transaction_manager);
            wp0.wall = null;
            return;
        }

        //*** Other interesting cases : there is an intersection */
        let wall_to_cut = wp0.wall;
        const all_walls = [wall_to_cut];
        const walls_to_keep = [];
        for (var nv = 0; nv < segments.length; nv++) {
            if (cn_dist(segments[nv], wall_to_cut.vertex_position(0)) > 0.001) {
                if (cn_dist(segments[nv], wall_to_cut.vertex_position(1)) > 0.001) {
                    const next_wall = this.scene.split_wall(wall_to_cut, new cn_vertex(segments[nv]), transaction_manager);
                    if (nv & 1) walls_to_keep.push(next_wall);
                    else walls_to_keep.push(wall_to_cut);
                    wall_to_cut = next_wall;
                    all_walls.push(next_wall);
                }
                else {
                    if ((nv & 1) == 0)
                        walls_to_keep.push(wall_to_cut);
                    break;
                }
            }
            else {
                if (nv & 1) walls_to_keep.push(wall_to_cut);
            }
        }
        all_walls.filter(w => !walls_to_keep.includes(w)).forEach(w => this.scene.remove_wall(w, transaction_manager));

        all_walls.filter(w => walls_to_keep.includes(w)).forEach(w => {
            const new_ww = new cna_wall(w);
            new_ww.update();
            this.wall_objects.push(new_ww);
        })
        wp0.wall = null;
    }

    /**
     * Analyse spaces
     * @param {cn_transaction_manager} transaction_manager
     */
    _analyse_spaces(transaction_manager) {
        if (this.scene.walls.length == 0) {
            if (transaction_manager) transaction_manager.push_item_set(this.scene, "spaces");
            const outside_space = cn_space.outside_space(this.scene);
            this.scene.spaces = [outside_space];
            return;
        }
        const box = this.scene.get_bounding_box(false, false, 0, false);

        //*** Cleanse space lines */
        this.scene.walls.forEach(w => {
            w.old_space_segments = w.space_segments;
            w.space_segments = [];
        });

        //*** Compute full polygon = bounding polygon - shapes of all walls */
        const full_polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
        full_polygon.add_contour([
            cnx_clone(cn_add(box.posmin, [-1, -1])),
            cnx_clone(cn_add(box.posmin, [box.size[0] + 2, -1])),
            cnx_clone(cn_add(box.posmin, [box.size[0] + 2, box.size[1] + 2])),
            cnx_clone(cn_add(box.posmin, [-1, box.size[1] + 2]))]);
        this.wall_objects.forEach(wo => full_polygon.substracts(wo.extended_shape));

        //*** Space polygons = split of full polygon */
        const space_polygons = full_polygon.split().filter(pg => pg.get_area() > 0.001);
        space_polygons.forEach(pg => pg.offset(0.01));

        //*** Extract outer polygon */
        let outer_polygon_index = -1;
        let max_area = 0;
        space_polygons.forEach((pg, i) => {
            const bb = pg.get_bounding_box();
            const a = bb.size[0] * bb.size[1];
            if (a > max_area) {
                max_area = a;
                outer_polygon_index = i;
            }
        })
        const outer_polygon = new fh_polygon([0, 0, 0], [0, 0, 1]);
        outer_polygon.add_contour([
            cnx_clone(cn_add(box.posmin, [-0.9, -0.9])),
            cnx_clone(cn_add(box.posmin, [box.size[0] + 1.8, -0.9])),
            cnx_clone(cn_add(box.posmin, [box.size[0] + 1.8, box.size[1] + 1.8])),
            cnx_clone(cn_add(box.posmin, [-0.9, box.size[1] + 1.8]))]);
        outer_polygon.substracts(space_polygons[outer_polygon_index]);
        space_polygons.splice(outer_polygon_index, 1);
        const outer_space = this.scene.spaces.find(sp => sp.outside);
        outer_space.set_inner_polygon(outer_polygon, transaction_manager);
        outer_space.build_space_segments(transaction_manager);
        const new_spaces = [outer_space];

        //*** Initialize polygons */
        space_polygons.forEach(pg => {
            const bb = pg.get_bounding_box();
            const box_new = new cn_box();
            box_new.enlarge_point(bb.position);
            box_new.enlarge_point(cn_add(bb.position, bb.size));
            pg["cn_box"] = box_new;
            pg["intersection"] = [];
        });

        //*** Find intersections between old spaces and new polygons */
        function compare_areas(pg1, pg0) {
            return pg0.get_area() - pg1.get_area();
        }
        const old_spaces = this.scene.spaces.filter(sp => !sp.outside);
        old_spaces.forEach(sp_old => {
            sp_old["intersection"] = [];
            const box_old = sp_old.get_bounding_box();
            space_polygons.forEach(pg => {
                const box_new = pg["cn_box"];
                if (box_old.intersects(box_new)) {
                    const intersection = sp_old.get_inner_polygon().clone();
                    intersection.intersects(pg);
                    if (intersection.get_area() > 0) {
                        intersection["old_space"] = sp_old;
                        intersection["new_polygon"] = pg;
                        sp_old["intersection"].push(intersection);
                        pg["intersection"].push(intersection);
                    }
                }
            });
            sp_old["intersection"].sort(compare_areas);
            sp_old["old_space_segments"] = sp_old.space_segments;
        });

        //*** Build new spaces */
        space_polygons.sort(compare_areas);
        space_polygons.forEach(pg => {
            const intersection_list = pg["intersection"];
            intersection_list.sort(compare_areas);
            const old_intersection_spaces = intersection_list.map(int => int["old_space"]);
            let old_space = null;
            if (old_intersection_spaces.length == 1) {
                old_space = old_intersection_spaces[0];
            }
            else if (old_intersection_spaces.length > 0) {
                old_space = old_intersection_spaces.find(sp => sp["intersection"].length == 1 && !new_spaces.includes(sp));
                if (!old_space) old_space = old_intersection_spaces[0];
            }

            let space = null;
            if (!old_space || new_spaces.includes(old_space)) {
                space = new cn_space(this.scene);
                space.space_type = this.building.get_space_types().find(st => !st.space_usage && st.heated)
                new_spaces.push(space)
                space.set_inner_polygon(pg); //*** no need for transaction here, this is a new space */
                if (old_space)
                    space.copy_parameters(old_space);
            }
            else {
                space = old_space;
                space.set_inner_polygon(pg, transaction_manager);
                new_spaces.push(space);
            }
            space.build_space_segments(transaction_manager);
            old_intersection_spaces.forEach(os => {
                if (os["intersection"][0] == pg && os != space) {
                    space.collect_space_data(os);
                    space.collect_facing(os["old_space_segments"]);
                }
            });
        });

        //*** Build indices */
        if (transaction_manager) transaction_manager.push_item_set(this.scene, "space_index");
        new_spaces.forEach(sp => { if (sp.index >= this.scene.space_index) this.scene.space_index = sp.index });
        new_spaces.filter(sp => !sp.outside && sp.index < 0).forEach(sp => {
            this.scene.space_index++;
            sp.index = this.scene.space_index;
        });

        if (transaction_manager) transaction_manager.push_item_set(this.scene, "spaces");
        this.scene.spaces = new_spaces;

        //*** Build wall interfaces */
        this.scene.walls.forEach(w => {
            w.concile_facings(w.old_space_segments);
            w.build_wall_interfaces();
        });

        this.scene.update_spaces();
    }

    /**
     * Unitary monkey test
     */
    monkey_test(nb_walls = 50) {

        this.building.transaction_manager.push_transaction("Monkey test");
        this.building.transaction_manager.push_item_set(this.scene, ["vertices", "walls"]);

        this.scene.vertices = [];
        this.scene.walls = [];

        //*** Build wall types */
        const thicknesses = [0, 0.08, 0.2, 0.5];
        const wall_types = [];
        thicknesses.forEach(thickness => {
            var wall_type = this.building.get_wall_types().find(w => w.thickness == thickness);
            if (!wall_type) {
                wall_type = new cn_wall_type(this.building);
                wall_type.name = 'Mur ' + (0.1 * Math.round(thickness * 1000)).toFixed(1) + 'cm';
                wall_type.layers = [cn_material.material_by_code(this.building, 'unknown', thickness)];
                this.building.element_types.push(wall_type);
            }
            wall_types.push(wall_type);
        });

        this.test_number++;
        let wall_type = wall_types[2];
        if (this.test_number == 0) {
            this.scene.vertices.push(new cn_vertex([-0.1, 0]));
            this.scene.vertices.push(new cn_vertex([5, 0]));
            this.scene.vertices.push(new cn_vertex([0, 0.1]));
            this.scene.vertices.push(new cn_vertex([0, -5]));
            this.scene.walls.push(new cn_wall(this.scene.vertices[0], this.scene.vertices[1], wall_type, CN_MIDDLE, this.scene));
            this.scene.walls.push(new cn_wall(this.scene.vertices[2], this.scene.vertices[3], wall_type, CN_MIDDLE, this.scene));
        }
        else if (this.test_number == 1) {
            this.scene.vertices.push(new cn_vertex([-5, 0]));
            this.scene.vertices.push(new cn_vertex([5, 0]));
            this.scene.walls.push(new cn_wall(this.scene.vertices[0], this.scene.vertices[1], wall_type, CN_MIDDLE, this.scene));
        }
        else if (this.test_number == 2) {
            this.scene.vertices.push(new cn_vertex([-5, 0]));
            this.scene.vertices.push(new cn_vertex([5, 0]));
            this.scene.vertices.push(new cn_vertex([-5, -5]));
            this.scene.vertices.push(new cn_vertex([5, -5]));
            this.scene.vertices.push(new cn_vertex([-5, 5]));
            this.scene.vertices.push(new cn_vertex([5, 5]));
            this.scene.vertices.push(new cn_vertex([0, 10]));
            this.scene.vertices.push(new cn_vertex([0, -10]));
            this.scene.walls.push(new cn_wall(this.scene.vertices[0], this.scene.vertices[1], wall_type, CN_MIDDLE, this.scene));
            this.scene.walls.push(new cn_wall(this.scene.vertices[2], this.scene.vertices[3], wall_type, CN_MIDDLE, this.scene));
            this.scene.walls.push(new cn_wall(this.scene.vertices[4], this.scene.vertices[5], wall_type, CN_MIDDLE, this.scene));
            this.scene.walls.push(new cn_wall(this.scene.vertices[6], this.scene.vertices[7], wall_type, CN_MIDDLE, this.scene));
            this.scene.walls.push(new cn_wall(this.scene.vertices[0], this.scene.vertices[2], wall_type, CN_MIDDLE, this.scene));
        }
        else if (this.test_number == 3) {
            this._test_overlap(wall_type, wall_type, 0)
        }
        else if (this.test_number == 4) {
            this._test_overlap(wall_type, wall_types[3], 0)
        }
        else if (this.test_number == 5) {
            this._test_overlap(wall_types[0], wall_types[3], 0)
        }
        else if (this.test_number == 6) {
            this._test_overlap(wall_types[2], wall_types[3], 0.05)
        }
        else {
            cn_seed(0.65487 + 0.01 * this.test_number);
            //*** build random vertices */
            for (var n = 0; n < nb_walls / 4 + 4; n++)
                this.scene.vertices.push(new cn_vertex([-10 + 20 * cn_random(), -10 + 20 * cn_random()], false));

            //*** Build random walls */
            for (var n = 0; n < nb_walls; n++) {
                wall_type = wall_types[Math.floor(cn_random() * wall_types.length) % wall_types.length];
                const v0 = this.scene.vertices[Math.floor(cn_random() * this.scene.vertices.length) % this.scene.vertices.length];
                var v1 = v0;
                while (v1 == v0)
                    v1 = this.scene.vertices[Math.floor(cn_random() * this.scene.vertices.length) % this.scene.vertices.length];
                this.scene.walls.push(new cn_wall(v0, v1, wall_type, CN_MIDDLE, this.scene));
            }
        }

        this.process();
    }

    _test_overlap(wall_type0, wall_type1, dy) {
        const opening_type = this.building.get_opening_types()[0];

        let nv = 0;
        for (var y = -6; y <= 6; y++) {
            this.scene.vertices.push(new cn_vertex([-2.5, y]));
            this.scene.vertices.push(new cn_vertex([2.5, y]));
            this.scene.vertices.push(new cn_vertex([-2 + y, y - dy]));
            this.scene.vertices.push(new cn_vertex([2 + y, y + dy]));
            const wall0 = new cn_wall(this.scene.vertices[nv], this.scene.vertices[nv + 1], wall_type0, CN_MIDDLE, this.scene);
            const wall1 = new cn_wall(this.scene.vertices[nv + 2], this.scene.vertices[nv + 3], wall_type1, CN_MIDDLE, this.scene);
            wall0.openings.push(new cn_opening(opening_type));
            wall0.openings[0].position = 0.5;
            wall0.openings[0].wall = wall0;
            wall1.openings.push(new cn_opening(opening_type));
            wall1.openings[0].position = 0.5;
            wall1.openings[0].wall = wall1;
            this.scene.walls.push(wall0);
            this.scene.walls.push(wall1);
            nv += 4;
        }
    }
}

